import {
  Shield, Globe, Users, Award, TrendingUp, Headphones, CheckCircle,
  Building2, Clock, Zap, Lock, Star, MapPin, Trophy, BarChart3, ArrowRight,
  ShieldCheck, Medal, Check, Layers, Crown, Gem, Sparkles
} from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-black to-slate-900 pt-32 pb-20 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-grid-white/5"></div>
        <div className="absolute top-20 left-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute bottom-20 right-10 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>

        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold">
            🤝 Professional Trading & Profit-Sharing Partnership
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight">
            About{" "}
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
              KESTREN
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-12">
            We are a team of full-time professional traders. You invest, we trade
            with disciplined, risk-managed strategies, and we{" "}
            <span className="text-cyan-400 font-semibold">
              share the profits with you
            </span>{" "}
            through a transparent partnership model.
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: Clock,
                value: "7+",
                label: "Years of Trading",
                color: "from-yellow-400 to-orange-500",
              },
              {
                icon: Users,
                value: "300+",
                label: "Active Partners",
                color: "from-cyan-400 to-blue-500",
              },
              {
                icon: BarChart3,
                value: "5+",
                label: "Core Strategies",
                color: "from-green-400 to-emerald-500",
              },
              {
                icon: Globe,
                value: "3",
                label: "Major Markets",
                color: "from-purple-400 to-pink-500",
              },
            ].map((stat, i) => (
              <div
                key={i}
                className="group bg-gradient-to-br from-white/5 to-white/10 border border-white/20 rounded-2xl p-6 hover:border-cyan-500/50 transition-all hover:-translate-y-2"
              >
                <div
                  className={`w-12 h-12 mx-auto mb-4 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center group-hover:opacity-40 transition`}
                >
                  <stat.icon className="w-7 h-7 text-white" />
                </div>
                <p className="text-3xl font-extrabold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  {stat.value}
                </p>
                <p className="text-gray-400 text-sm mt-2 font-medium">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-gradient-to-b from-black via-slate-900/50 to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/5"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold">
                ✨ Our Story
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold">
                Traders First.{" "}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  Partners Always.
                </span>
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed">
                KESTREN began when a small group of professional traders started
                managing only their own capital. As results became consistent,
                close friends and family asked to participate — and that&apos;s
                how our{" "}
                <span className="text-cyan-400 font-bold">
                  profit-sharing partnership model
                </span>{" "}
                was born.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300 text-lg">
                    <strong className="text-white">
                      Disciplined, rule-based trading
                    </strong>{" "}
                    instead of emotional, impulse-driven decisions.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300 text-lg">
                    Focus on{" "}
                    <strong className="text-white">capital protection</strong>{" "}
                    first, profit growth second.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300 text-lg">
                    Clear communication, honest expectations, and{" "}
                    <strong className="text-white">
                      no guaranteed-return promises
                    </strong>
                    .
                  </p>
                </div>
              </div>
              <p className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                We don&apos;t just look for trades. We build long-term
                relationships.
              </p>
            </div>

            {/* Right Content - Partnership Types */}
            <div className="grid grid-cols-2 gap-5">
              {[
                {
                  title: "Working Professionals",
                  subtitle: "Who want passive growth on surplus income",
                },
                {
                  title: "Business Owners",
                  subtitle: "Who want their idle capital to work",
                },
                {
                  title: "HNIs & Angels",
                  subtitle: "Looking for diversification via trading",
                },
                {
                  title: "Long-Term Partners",
                  subtitle: "Focused on steady returns, not quick flips",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="group bg-gradient-to-br from-white/5 to-white/10 border border-white/20 rounded-2xl p-6 text-center hover:border-cyan-500/50 transition-all hover:-translate-y-2"
                >
                  <div className="w-12 h-12 mx-auto mb-4 bg-cyan-500/20 rounded-xl flex items-center justify-center group-hover:bg-cyan-500/30 transition">
                    <Users className="w-7 h-7 text-cyan-400" />
                  </div>
                  <p className="font-bold text-white text-lg mb-1">
                    {item.title}
                  </p>
                  <p className="text-sm text-gray-400">{item.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
              What We{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Stand For
              </span>
            </h2>
            <p className="text-xl text-gray-400">
              The principles behind every trade we place and every partnership
              we build
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Mission",
                desc: "To grow our partners’ capital through disciplined, risk-managed trading while maintaining full transparency and trust.",
                icon: Shield,
                color: "from-cyan-400 to-blue-500",
              },
              {
                title: "Vision",
                desc: "To become the most trusted trading-partnership brand for individuals who want professionals to trade on their behalf.",
                icon: Globe,
                color: "from-purple-400 to-pink-500",
              },
              {
                title: "Values",
                desc: "Discipline • Integrity • Transparency • Risk Management • Long-Term Thinking — these guide every decision we make.",
                icon: Star,
                color: "from-yellow-400 to-orange-500",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="group relative bg-gradient-to-br from-white/5 to-white/10 border border-white/20 rounded-2xl p-8 backdrop-blur-sm hover:border-cyan-500/50 transition-all hover:-translate-y-2 overflow-hidden"
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                ></div>

                <div className="relative">
                  <div
                    className={`w-16 h-16 bg-gradient-to-br ${item.color}  rounded-2xl flex items-center justify-center mb-6 group-hover:opacity-40 group-hover:scale-110 transition-all`}
                  >
                    <item.icon className="w-9 h-9 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Features Grid */}
      <section className="py-24 bg-gradient-to-b from-black via-slate-900/50 to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/5"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
              Why Investors Choose{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                KESTREN
              </span>
            </h2>
            <p className="text-xl text-gray-400">
              Not a tip group, not a signal channel — a real trading partnership
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              "Transparent profit-sharing model",
              "Capital-first, profit-next mindset",
              "No guaranteed-return claims",
              "Written terms & clear expectations",
              "Strict risk per trade & max drawdown",
              "Disciplined, rules-based execution",
              "Access to performance summaries",
              "Regular communication & support",
              "Diversified strategies across markets",
              "No complex jargon, simple explanations",
              "Aligned incentives — we earn when you do",
              "Long-term partnership focus, not quick churn",
            ].map((feat, i) => (
              <div
                key={i}
                className="flex items-center gap-4 bg-gradient-to-br from-white/5 to-white/10 border border-white/20 rounded-xl p-5 hover:border-cyan-500/50 hover:bg-white/10 transition-all group"
              >
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-green-500/30 transition">
                  <Check className="w-5 h-5 text-green-400" />
                </div>
                <span className="font-medium text-white text-base">
                  {feat}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security / Transparency Section */}
      <section className="py-24 bg-black">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-gradient-to-br from-white/5 to-white/10 border border-white/20 rounded-3xl p-12 backdrop-blur-sm text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-500 opacity-20 rounded-2xl flex items-center justify-center">
              <ShieldCheck className="w-12 h-12 text-white" />
            </div>

            <h2 className="text-4xl md:text-5xl font-extrabold mb-6">
              Your Capital{" "}
              <span className="bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">
                Comes First
              </span>
            </h2>

            <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-8 leading-relaxed">
              We believe in{" "}
              <span className="text-cyan-400 font-semibold">
                transparency, control, and clarity
              </span>
              . There are no hidden terms, no unrealistic promises, and no
              pressure. Before you invest, we walk you through the{" "}
              <span className="text-white font-semibold">risks</span>,{" "}
              <span className="text-white font-semibold">process</span>, and{" "}
              <span className="text-white font-semibold">
                how profit sharing works
              </span>
              .
            </p>

            <div className="flex flex-wrap justify-center gap-8 text-gray-300">
              <span className="flex items-center gap-2 bg-white/5 px-5 py-3 rounded-xl">
                <Lock className="w-6 h-6 text-cyan-400" /> Clear documentation
                of terms
              </span>
              <span className="flex items-center gap-2 bg-white/5 px-5 py-3 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-400" /> Regular
                performance updates
              </span>
              <span className="flex items-center gap-2 bg-white/5 px-5 py-3 rounded-xl">
                <Building2 className="w-6 h-6 text-blue-400" /> Focus on risk
                management
              </span>
            </div>

            <p className="text-sm text-gray-500 mt-8 max-w-3xl mx-auto">
              *Trading in financial markets involves risk of loss. Returns are
              not guaranteed. Our role is to manage that risk professionally and
              align our incentives with yours through a profit-sharing model.
            </p>
          </div>
        </div>
      </section>

      {/* How We Work Section (replaces “Trading Platforms” meaning-wise) */}
      <section className="py-24 bg-gradient-to-b from-black via-slate-900/50 to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/5"></div>
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
              How We{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Work With You
              </span>
            </h2>
            <p className="text-xl text-gray-400">
              A simple, structured process behind every partnership
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Research & Strategy",
                desc: "We use a combination of technical and price-action based systems, tested over different market conditions.",
                icon: Layers,
                color: "from-cyan-400 to-blue-500",
              },
              {
                name: "Execution & Risk",
                desc: "All trades follow defined risk rules — position sizing, maximum loss per day, and strict stop-loss discipline.",
                icon: BarChart3,
                color: "from-purple-400 to-pink-500",
              },
              {
                name: "Reporting & Support",
                desc: "You receive periodic updates, performance overviews, and a clear view of how your capital is being handled.",
                icon: Headphones,
                color: "from-yellow-400 to-orange-500",
              },
            ].map((plat, i) => (
              <div
                key={i}
                className="group bg-gradient-to-br from-white/5 to-white/10 border border-white/20 rounded-2xl p-8 text-center hover:border-cyan-500/50 transition-all hover:-translate-y-2"
              >
                <div
                  className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br ${plat.color}  rounded-2xl flex items-center justify-center group-hover:opacity-40 group-hover:scale-110 transition-all`}
                >
                  <plat.icon className="w-12 h-12 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors">
                  {plat.name}
                </h3>
                <p className="text-gray-400 leading-relaxed">{plat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 bg-gradient-to-r from-cyan-600 via-blue-600 to-purple-600 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer"></div>

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="mb-8">
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-yellow-300" />
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6">
            Ready to Discuss Your Investment?
          </h2>

          <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            Schedule a{" "}
            <strong className="text-yellow-300">free, no-obligation call</strong>{" "}
            with our trading team. Understand the risks, the process, and how
            profit sharing works — before you decide anything.
          </p>

          <a
            href="/contact"
            className="inline-flex items-center gap-4 bg-white text-blue-600 font-extrabold text-xl md:text-2xl px-12 py-6 rounded-2xl hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-2xl"
          >
            <Headphones className="w-8 h-8" />
            TALK TO A TRADER
            <ArrowRight className="w-8 h-8" />
          </a>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-white/90">
            <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
              <Lock className="w-5 h-5" /> No spam, no pressure
            </span>
            <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
              <CheckCircle className="w-5 h-5" /> Honest risk explanation
            </span>
            <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
              <Clock className="w-5 h-5" /> Quick response from the team
            </span>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
        
        .bg-grid-white\\/5 {
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 50px 50px;
        }
        
        .bg-grid-white\\/10 {
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
    </div>
  );
}
